// PeriodTable.jsx - Table Component for Displaying KPI Periods

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Menu,
  Link,
  styled,
  alpha,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { deletePeriodRequest } from '../redux.actions';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { TABLE_COLUMNS, COLUMN_WIDTHS, DISPLAY_DATE_FORMAT } from '../common';

const StyledMenuBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2)
}));

const menuLinkStyles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '32px',
    padding: '8px 12px',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: '4px',
    color: 'primary.main',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 400,
    cursor: 'pointer',
    pl: 3,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        textDecoration: 'none',
        borderColor: theme.palette.primary.dark,
        color: theme.palette.primary.dark
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
});

const deleteLinkStyles = (theme) => ({
    ...menuLinkStyles(theme),
    borderColor: 'error.main',
    color: 'error.main',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        textDecoration: 'none',
        borderColor: 'error.dark',
        color: 'error.dark'
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.error.main, 0.1)
    }
});

const PeriodTable = ({ periods, loading, pagination, onEdit, onPageChange, onRowsPerPageChange }) => {
  const dispatch = useDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);

  const handleDeleteClick = (period) => {
    setPeriodToDelete(period);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (periodToDelete) {
      dispatch(deletePeriodRequest(periodToDelete.id));
      setDeleteDialogOpen(false);
      setPeriodToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPeriodToDelete(null);
  };

  const openMenu = (event, period) => {
    setMenuAnchorEl(event.currentTarget);
    setActivePeriod(period);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setActivePeriod(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return dayjs(dateString).format(DISPLAY_DATE_FORMAT);
    } catch {
      return dateString;
    }
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const days = end.diff(start, 'day');
    return `${days} days`;
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {TABLE_COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth, width: COLUMN_WIDTHS[column.id] }}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: 'action.hover',
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {periods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_COLUMNS.length} align="center" sx={{ py: 4 }}>
                  No KPI periods found
                </TableCell>
              </TableRow>
            ) : (
              periods.map((period) => (
                <TableRow
                  key={period.id}
                  hover
                  sx={(theme) => ({
                    '&:last-child td': { border: 0 },
                    '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.06) }
                  })}
                >
                  <TableCell>
                    <strong>{period.name}</strong>
                  </TableCell>
                  <TableCell>{formatDate(period.startDate)}</TableCell>
                  <TableCell>{formatDate(period.endDate)}</TableCell>
                  <TableCell>
                    {period.durationDays || calculateDuration(period.startDate, period.endDate)}
                  </TableCell>
                  <TableCell>{period.updatedBy || period.createdBy || '-'}</TableCell>
                  <TableCell>{formatDate(period.updatedAt)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="More" placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => openMenu(e, period)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        id="period-actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'period-actions-button'
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <StyledMenuBox>
          <Link
            component="button"
            onClick={(e) => {
              e.preventDefault();
              if (activePeriod) onEdit(activePeriod);
              closeMenu();
            }}
            sx={(theme) => menuLinkStyles(theme)}
          >
            <EditIcon sx={{ marginRight: 1, fontSize: '18px' }} />
            Edit
          </Link>
          <Link
            component="button"
            onClick={(e) => {
              e.preventDefault();
              if (activePeriod) handleDeleteClick(activePeriod);
              closeMenu();
            }}
            sx={(theme) => deleteLinkStyles(theme)}
          >
            <DeleteIcon sx={{ marginRight: 1, fontSize: '18px' }} />
            Delete
          </Link>
        </StyledMenuBox>
      </Menu>

      <TablePagination
        component="div"
        count={pagination.totalItems || 0}
        page={(pagination.offset || 1) - 1}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        rowsPerPage={pagination.limit || 10}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Rows per page:"
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          '& .MuiTablePagination-toolbar': {
            justifyContent: 'space-between',
            paddingLeft: 2,
            paddingRight: 2
          },
          '& .MuiTablePagination-spacer': {
            display: 'none'
          },
          '& .MuiTablePagination-selectLabel': {
            margin: 0
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
            marginLeft: 'auto'
          }
        }}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        periodName={periodToDelete?.name}
      />
    </>
  );
};

export default PeriodTable;
